import {ReactElement} from 'react';
import Dashboard from '@modules/home/main/dashboard/Dashboard';
import ModulesMenuHomePage from '@modules/home/main/modules-menu/ModulesMenuHomePage';
import {AppModules} from '@shared/constants/Modules';
import {Routes, Route, Navigate, useLocation} from 'react-router-dom';
import {AuthenticatedRoute} from './AuthStateRoutes';
import {PRIVATE_ROUTES, RouteInfo} from './RouteConstants';
import {BASE_ROUTE, BASE_ROUTE_PAD, BASE_ROUTE_WIDGETS} from './BaseRoute';
import SubModulesMenuHomePage from '@modules/home/main/sub-modules-menu/SubModulesMenuHomePage';
import PadsHomePage from '@modules/home/main/pads/PadsHomePage';
import WidgetsHomePage from '@modules/home/main/widgets/WidgetsHomePage';
import {useAppContext} from '@context/AppContext';
import MainLayout from 'src/layouts/main';
import CompactLayout from 'src/layouts/compact';
import {NotFoundView} from 'src/sections/error';
import TestPage from '@modules/home/main/modules-menu/course-catalog/ModulePageComponents/PreviewModule/OnlineTestPreview/IntroTestPreview';
import PreviewParamsPage from '@modules/home/main/modules-menu/course-catalog/ModulePageComponents/PreviewModule/OnlineTestPreview/PreviewParamsPage';
import TestDetailsPage from '@modules/home/main/modules-menu/course-catalog/ModulePageComponents/PreviewModule/Preview';
import CourseCatalogComponent from '@modules/home/main/modules-menu/course-catalog/CourseCatalogComponent';
import UAMDashboard from '@modules/home/main/uam';

// Create a map of routes to modules
// Only include the high level modules for authenticated routes
export const PRIVATE_ROUTES_ARR: Array<RouteInfo> = [
  {
    route: PRIVATE_ROUTES.DASHBOARD,
    requiresAuthCheck: true,
    Component: Dashboard,
    id: '0',
    hasSubRoutes: false,
    module: AppModules.DASHBOARD
  },
  
  {
    route: `${PRIVATE_ROUTES.MODULE}`,
    requiresAuthCheck: true,
    Component: ModulesMenuHomePage,
    id: '1',
    hasSubRoutes: true,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.MODULE}/:module_name`,
    requiresAuthCheck: true,
    Component: ModulesMenuHomePage,
    id: '2',
    hasSubRoutes: false,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.MODULE}/:module_name/submodule`,
    requiresAuthCheck: true,
    Component: SubModulesMenuHomePage,
    id: '3',
    hasSubRoutes: true,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.MODULE}/:module_name/submodule/:submodule_name`,
    requiresAuthCheck: true,
    Component: SubModulesMenuHomePage,
    id: '4',
    hasSubRoutes: false,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.APPLICATION}/:application_id/${BASE_ROUTE_PAD}`,
    requiresAuthCheck: true,
    Component: PadsHomePage,
    id: '5',
    hasSubRoutes: false,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.APPLICATION}/:application_id/${BASE_ROUTE_PAD}/:pad_id`,
    requiresAuthCheck: true,
    Component: PadsHomePage,
    id: '6',
    hasSubRoutes: false,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.APPLICATION}/:application_id/${BASE_ROUTE_PAD}/:pad_id/${BASE_ROUTE_WIDGETS}`,
    requiresAuthCheck: true,
    Component: WidgetsHomePage,
    id: '7',
    hasSubRoutes: false,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.MODULE}/:module_name/Preview`,
    requiresAuthCheck: true,
    Component: TestDetailsPage,
    id: '12',
    hasSubRoutes: true,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.MODULE}/:module_name/OnlineTest`,
    requiresAuthCheck: true,
    Component: TestPage,
    id: '13',
    hasSubRoutes: true,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.MODULE}/:module_name/OnlineTest/:submodule_name`,
    requiresAuthCheck: true,
    Component: PreviewParamsPage,
    id: '14',
    hasSubRoutes: false,
    module: AppModules.DASHBOARD
  },
  {
    route: `${PRIVATE_ROUTES.MODULE}/course_management/new`,
    requiresAuthCheck: true,
    Component: CourseCatalogComponent,
    id: '13',
    hasSubRoutes: true,
    module: AppModules.DASHBOARD
  },
  {
    route: PRIVATE_ROUTES.UAM,
    requiresAuthCheck: true,
    Component: UAMDashboard,
    id: '14',
    hasSubRoutes: false,
    module: AppModules.USER_ACCESS
  },
];

const PrivateRoutes = (): JSX.Element => {
  const location = useLocation();
  const {
    appState: {applicationName}
  } = useAppContext();

  const renderRoutesByModule = (
    Component: any,
    module?: AppModules
  ): ReactElement => {
    // Attach the corresponding context here
    return <Component />;

    // TODO: commented out the below code - because we are always returning <Component/> in all the cases
    // switch (module) {
    //   case AppModules.DASHBOARD:
    //     return <Component />;
    //   default:
    //     return <Component />;
    // }
  };

  return (
    <Routes>
      {PRIVATE_ROUTES_ARR.map(routes => {
        const {route, Component, hasSubRoutes, module} = routes;
        return (
          <Route
            key={routes.id}
            path={`/${BASE_ROUTE}/${route}${hasSubRoutes ? '/*' : ''}`}
            element={
              `/${BASE_ROUTE}/${PRIVATE_ROUTES.DASHBOARD}` ===
              location?.pathname ? (
                <MainLayout>
                  {applicationName === 'App_Rationalization' ? (
                    <>{renderRoutesByModule(Component, module)}</>
                  ) : (
                    <AuthenticatedRoute>
                      {renderRoutesByModule(Component, module)}
                    </AuthenticatedRoute>
                  )}
                </MainLayout>
              ) : (
                <>
                  {applicationName === 'App_Rationalization' ? (
                    <>{renderRoutesByModule(Component, module)}</>
                  ) : (
                    <AuthenticatedRoute>
                      {renderRoutesByModule(Component, module)}
                    </AuthenticatedRoute>
                  )}
                </>
              )
            }
          />
        );
      })}
      <Route
        path={`/${BASE_ROUTE}/404`}
        element={
          <CompactLayout>
            <NotFoundView />
          </CompactLayout>
        }
      />
      <Route
        path="*"
        element={<Navigate to={`/${BASE_ROUTE}/` + PRIVATE_ROUTES.DASHBOARD} />}
      />
    </Routes>
  );
};

export default PrivateRoutes;
